import { requiresExplicitVerificationPolicy } from '@/modules/agreements/shared';
import type { DocumentDraft } from '@/modules/agreements/platform/pages/document-create-page/types';

/**
 * The document key's shape.
 *
 * Mirrors the API's `AgreementDocumentKeySchema`: lowercase, starts with a
 * letter, then letters/digits/underscores. NO HYPHENS — the key is permanent and
 * is used as an identifier, so a hyphen would make it ambiguous with a slug.
 */
const DOCUMENT_KEY = /^[a-z][a-z0-9_]{1,63}$/;

/** A BCP-47-ish locale tag: `en`, `en-IN`. */
const LOCALE = /^[a-z]{2}(-[A-Z]{2})?$/;

/**
 * Whether step 1 is complete.
 *
 * The key is checked against the API's own pattern rather than a looser one, so
 * a key that passes here is not refused by the server a step later. A key that
 * is merely "non-empty" would let the operator reach step 3 before learning the
 * key was never acceptable.
 */
export function isDetailsStepValid(draft: DocumentDraft): boolean {
  return DOCUMENT_KEY.test(draft.documentKey) && draft.title.trim().length > 0;
}

/**
 * Whether step 2 is complete.
 *
 * Content is required: a version with no text is a version that binds nobody to
 * anything, and the API refuses it (`content: z.string().min(1)`).
 */
export function isContentStepValid(draft: DocumentDraft): boolean {
  return (
    LOCALE.test(draft.locale) &&
    draft.versionTitle.trim().length > 0 &&
    draft.content.trim().length > 0
  );
}

/**
 * Whether step 3 is complete.
 *
 * THE EXPLICIT-POLICY RULE. For a VENDOR SIGNATURE agreement (audience `tenant`,
 * action `signature`) the API refuses an absent `verificationPolicy` with
 * `INVALID_AGREEMENT_REQUIREMENT` rather than silently defaulting to
 * `session_reattested`. The operator must therefore choose one on purpose, and
 * this check is what stops the flow from submitting a body the server will
 * reject.
 *
 * For every other shape an absent policy is allowed and the server defaults it.
 */
export function isRequirementStepValid(draft: DocumentDraft): boolean {
  if (draft.audience === 'both') return false;
  const explicitRequired = requiresExplicitVerificationPolicy(
    draft.audience,
    draft.requirementAction,
  );
  return !explicitRequired || draft.verificationPolicy !== '';
}

/**
 * The first step that is not complete, or -1 when all three are.
 *
 * Used by the submit to re-check EVERY step rather than trusting that the
 * operator passed through them in order — they can jump back and edit step 1
 * after reaching step 3, and a stale pass must not carry the submission.
 */
export function firstIncompleteStep(draft: DocumentDraft): number {
  if (!isDetailsStepValid(draft)) return 0;
  if (!isContentStepValid(draft)) return 1;
  if (!isRequirementStepValid(draft)) return 2;
  return -1;
}
